import {
  createHighlights,
  clearHighlights,
  scrollToHighlight,
} from './highlighter';
import type { BoundingBox, Claim, ClaimLocation } from './types';
import { trackClick } from 'js/utils/trackClick';

// Set in single_work/show.html.slim
declare global {
  interface Window {
    pageData?: {
      highlighted_claims?: Claim[];
      [key: string]: unknown;
    };
  }
}

// Function to initialize the claim to highlight functionality
export const initializeClaimToHighlight = (claims: Claim[], workId: number) => {
  if (!claims?.length) {
    return null;
  }

  const handleClaimClick = (claimNumber: number) => {
    const claim = claims.find((c) => c.number === claimNumber);
    if (!claim || !claim.locations) {
      return;
    }

    clearHighlights();

    document.querySelectorAll('.claim-card').forEach((card) => {
      card.classList.remove('claim-card--active');
    });

    const claimCards = document.querySelectorAll('.claim-card');
    claimCards.forEach((card) => {
      const cardClaimNumber = card.getAttribute('data-claim-number');
      if (cardClaimNumber && parseInt(cardClaimNumber) === claimNumber) {
        card.classList.add('claim-card--active');
      }
    });

    // Normalize locations to always be an array
    const locationsArray = Array.isArray(claim.locations)
      ? claim.locations
      : [claim.locations];

    // Convert locations to BoundingBox format
    const boundingBoxes: BoundingBox[] = locationsArray.map(
      (location: ClaimLocation) => ({
        page: location.page,
        xmin: location.x,
        ymin: location.y,
        width: location.width,
        height: location.height,
      }),
    );

    // Find the first element in logical reading order (lowest page, then lowest x, then lowest y)
    const firstLocation = boundingBoxes.reduce((first, current) => {
      if (current.page !== first.page) {
        return current.page < first.page ? current : first;
      }
      if (current.xmin !== first.xmin) {
        return current.xmin < first.xmin ? current : first;
      }
      return current.ymin < first.ymin ? current : first;
    });

    // Extract page dimensions from the first location (assuming all locations have same page dimensions)
    const pageDimensions = locationsArray[0]?.page_dimensions;

    if (boundingBoxes.length > 0) {
      // Scroll to the first location in logical reading order
      scrollToHighlight(firstLocation, pageDimensions);

      createHighlights(boundingBoxes, pageDimensions).catch((error) => {
        console.error('Failed to create highlights:', error);
      });
    }
  };

  const setupClaimCardHandlers = () => {
    const claimLinks = document.querySelectorAll('.claim-card__link');

    claimLinks.forEach((link) => {
      const claimNumber = link.getAttribute('data-claim-number');
      if (!claimNumber) return;

      const claimNumberInt = parseInt(claimNumber);

      link.addEventListener('click', (event) => {
        event.preventDefault();

        trackClick({
          source: 'claim_to_highlight_see_in_context',
          entityId: workId,
          extra: { claim_number: claimNumberInt },
        });

        handleClaimClick(claimNumberInt);
      });
    });
  };

  setupClaimCardHandlers();

  // Return cleanup function
  return () => {
    clearHighlights();
  };
};

export const initializeClaims = () => {
  const claims = window.pageData?.highlighted_claims;
  const workId = window.loswp?.work?.id;

  if (claims && Array.isArray(claims) && claims.length > 0 && workId) {
    initializeClaimToHighlight(claims, workId);
  }
};
